import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import {
  ReactFlow,
  FitViewOptions,
  Background,
  BackgroundVariant,
  NodeTypes,
  MiniMap,
  SelectionMode,
  Node,
  Edge,
  Connection,
  useReactFlow,
  useStore,
  useUpdateNodeInternals,
  XYPosition,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { SequenceNode } from "./SequenceNode";
import SequenceContextMenu from "./SequenceContextMenu";
import { useSequenceCanvas } from "./SequenceCanvas.hook";
import { getNodeAndAfter } from "./getNodeAndAfter";
import SequenceCustomEdge from "./SequenceCustomEdge";
import useSequenceCanvasSelection, {
  getSelectionInputMode,
  SequenceCanvasItem,
  SequenceCanvasSelection,
  SelectionInputMode,
} from "./SequenceCanvasSelection.hook";
import {
  getAnnotationIdsInMarquee,
  normalizeFlowRect,
} from "./SequenceCanvasSelectionGeometry";
import useShortcut, {
  isEditableElement,
  ShortCuts,
} from "../../hooks/UseShortcutsHook";
import { useSequenceCopyPaste } from "./useSequenceCopyPaste";
import {
  selectFocusedSequenceNodeId,
  selectSequenceCanvasCommand,
  selectSequenceCanvasViewport,
  selectSequencePositionPicker,
  selectShouldRestoreSequenceCanvasViewport,
  clearSequenceCanvasCommand,
  setSequenceCanvasSelection,
  setSequenceCanvasViewport,
  clearSequenceCanvasViewportRestore,
  setFocusedSequenceNodeId,
} from "../../app/slices/GlobalSlice";
import { RootState } from "../../app/Store";
import {
  selectActors,
  selectAllPropPresets,
  selectSelectedElementType,
} from "../../app/slices/TrainingSlice";
import SequenceSelectionColorToolbar from "./components/SequenceSelectionColorToolbar";
import SequenceCommentZoneLayer, {
  createCommentZoneFromNodes,
} from "./SequenceCommentZoneLayer";
import SequenceTextboxLayer, {
  createSequenceTextbox,
} from "./SequenceTextboxLayer";
import { useSequenceLayout } from "./layout/useSequenceLayout.hook";
import { TEMP_TARGET_NODE_ID } from "./SequenceCanvasTemporaryElements";
import {
  findSequenceCanvasMatches,
  SequenceCanvasSearchMatch,
  SequenceNodeType,
} from "./SequenceCanvasSearch";
import { createSearchLookupContext } from "../../models/interactions/SearchableInteractionValues";
import type { SequenceNodeData } from "./SequenceNode";
import {
  MIN_SEQUENCE_CANVAS_ZOOM,
  SequenceCanvasLodProvider,
} from "./SequenceCanvasLod";
import {
  isProtectedTakeSequenceTrigger,
  isSequenceRootNode,
  isTakeSequenceEditor,
  placeDefaultTakeEnd,
} from "./takeSequenceEditor";
import { useAppZoom } from "../../hooks/AppZoom.hook";

// The minimap is switched off for now; set to true to bring it back.
const SHOW_SEQUENCE_MINIMAP = false;

const fitViewOptions: FitViewOptions = {
  padding: 0.2,
};

// Component to handle zooming to focused nodes
function FocusNodeHandler() {
  const { fitView, getNode } = useReactFlow();
  const focusedNodeId = useSelector(selectFocusedSequenceNodeId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (focusedNodeId) {
      const node = getNode(focusedNodeId);
      if (node) {
        // Zoom to the node with some padding
        fitView({
          nodes: [{ id: focusedNodeId }],
          padding: 0.3,
          duration: 500,
        });
      }
      // Clear the focused node after zooming
      dispatch(setFocusedSequenceNodeId(null));
    }
  }, [focusedNodeId, fitView, getNode, dispatch]);

  return null;
}

// React Flow only re-measures a node when it sees it lose its size. Handed the node without
// `measured` right after measuring it, it can end up sized but without handle positions and
// never look again: the node's edges stop drawing and every marquee selects it. Keeping the
// size on our nodes (see the hook) makes that rare; this repairs it when it still happens,
// e.g. on a fresh mount before any size is known.
function RemeasureNodesWithoutHandles() {
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeIdsWithoutHandles = useStore((state) => {
    const ids: string[] = [];
    state.nodeLookup.forEach((node) => {
      if (
        !node.hidden &&
        node.measured.width &&
        node.measured.height &&
        !node.internals.handleBounds
      ) {
        ids.push(node.id);
      }
    });
    return ids.join("\n");
  });

  useEffect(() => {
    if (nodeIdsWithoutHandles) {
      updateNodeInternals(nodeIdsWithoutHandles.split("\n"));
    }
  }, [nodeIdsWithoutHandles, updateNodeInternals]);

  return null;
}

function FocusCanvasSearchMatchHandler({
  activeSearchMatch,
  searchFocusRequestId,
  onFocused,
}: {
  activeSearchMatch: SequenceCanvasSearchMatch | null;
  searchFocusRequestId: number;
  onFocused: () => void;
}) {
  const { setCenter } = useReactFlow();

  useEffect(() => {
    if (!activeSearchMatch || searchFocusRequestId === 0) {
      return;
    }

    if (activeSearchMatch.kind === "node") {
      onFocused();
      return;
    }

    const position: XYPosition =
      activeSearchMatch.kind === "commentZone"
        ? {
            x:
              activeSearchMatch.commentZone.position.x +
              activeSearchMatch.commentZone.size.width / 2,
            y:
              activeSearchMatch.commentZone.position.y +
              activeSearchMatch.commentZone.size.height / 2,
          }
        : {
            x:
              activeSearchMatch.textbox.position.x +
              activeSearchMatch.textbox.size.width / 2,
            y:
              activeSearchMatch.textbox.position.y +
              activeSearchMatch.textbox.size.height / 2,
          };

    setCenter(position.x, position.y, { zoom: 1.2, duration: 500 });
    onFocused();
  }, [activeSearchMatch, onFocused, searchFocusRequestId, setCenter]);

  return null;
}

function SequenceCanvasViewportPersistence({
  sequenceId,
  savedViewport,
}: {
  sequenceId: string;
  savedViewport: { x: number; y: number; zoom: number } | null;
}) {
  const { fitView, setViewport } = useReactFlow();
  const dispatch = useDispatch();
  const shouldRestoreViewport = useSelector(
    selectShouldRestoreSequenceCanvasViewport,
  );
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const previousSequenceIdRef = useRef(sequenceId);

  useEffect(() => {
    if (previousSequenceIdRef.current === sequenceId) {
      return;
    }

    previousSequenceIdRef.current = sequenceId;
    const frameId = window.requestAnimationFrame(() => {
      if (savedViewport) {
        setViewport(savedViewport, { duration: 0 });
        return;
      }

      fitView(fitViewOptions);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [fitView, savedViewport, sequenceId, setViewport]);

  useEffect(() => {
    if (
      !shouldRestoreViewport ||
      sequencePositionPicker.isOpen ||
      !savedViewport
    ) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      setViewport(savedViewport, { duration: 0 });
      dispatch(clearSequenceCanvasViewportRestore());
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    dispatch,
    savedViewport,
    sequencePositionPicker.isOpen,
    setViewport,
    shouldRestoreViewport,
  ]);

  return null;
}

function SequenceCanvasCommandBridge({
  commandType,
  commandRequestId,
  wrapperRef,
  selectedNodes,
  handleCopy,
  handleCut,
  handlePaste,
  handleDuplicate,
  onFormatLayout,
  onCreateCommentZone,
  onCreateTextbox,
  setSelection,
}: {
  commandType: string | null;
  commandRequestId: number;
  wrapperRef: React.RefObject<HTMLDivElement>;
  selectedNodes: any[];
  handleCopy: (options?: { force?: boolean }) => void;
  handleCut: (options?: { force?: boolean }) => void;
  handlePaste: (options?: {
    force?: boolean;
    useViewportCenter?: boolean;
  }) => Promise<void>;
  handleDuplicate: (options?: {
    force?: boolean;
    useViewportCenter?: boolean;
  }) => void;
  onFormatLayout: () => void;
  onCreateCommentZone: (zone: any) => void;
  onCreateTextbox: (textbox: any) => void;
  setSelection: (selection: SequenceCanvasSelection) => void;
}) {
  const dispatch = useDispatch();
  const { screenToFlowPosition, getNodes } = useReactFlow();
  const processedRequestIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      !commandType ||
      commandRequestId === 0 ||
      processedRequestIdRef.current === commandRequestId
    ) {
      return;
    }

    processedRequestIdRef.current = commandRequestId;

    const executeCommand = async () => {
      if (commandType === "formatLayout") {
        onFormatLayout();
      } else if (commandType === "copySelection") {
        handleCopy({ force: true });
      } else if (commandType === "cutSelection") {
        handleCut({ force: true });
      } else if (commandType === "pasteSelection") {
        await handlePaste({ force: true, useViewportCenter: true });
      } else if (commandType === "duplicateSelection") {
        handleDuplicate({ force: true, useViewportCenter: true });
      } else if (commandType === "createTextbox") {
        const bounds = wrapperRef.current?.getBoundingClientRect();
        if (!bounds) {
          return;
        }

        const centerPosition = screenToFlowPosition({
          x: bounds.left + bounds.width / 2 - 140,
          y: bounds.top + bounds.height / 2 - 60,
        });
        const textbox = createSequenceTextbox(centerPosition);
        onCreateTextbox(textbox);
        setSelection({
          nodeIds: [],
          commentZoneIds: [],
          textboxIds: [textbox.id],
        });
      } else if (commandType === "createCommentZone") {
        const measuredNodesById = new Map(
          getNodes().map((node) => [node.id, node]),
        );
        const selectedMeasuredNodes = selectedNodes
          .map((node) => measuredNodesById.get(node.id))
          .filter(Boolean);
        const zone = createCommentZoneFromNodes(selectedMeasuredNodes as any[]);
        if (!zone) {
          return;
        }
        onCreateCommentZone(zone);
        setSelection({
          nodeIds: [],
          commentZoneIds: [zone.id],
          textboxIds: [],
        });
      }
    };

    void executeCommand().finally(() => {
      dispatch(clearSequenceCanvasCommand());
    });
  }, [
    commandRequestId,
    commandType,
    dispatch,
    getNodes,
    handleCopy,
    handleCut,
    handleDuplicate,
    handlePaste,
    onFormatLayout,
    onCreateCommentZone,
    onCreateTextbox,
    screenToFlowPosition,
    selectedNodes,
    setSelection,
    wrapperRef,
  ]);

  return null;
}

export default function SequenceCanvas() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { screenToFlowPosition } = useReactFlow();
  const appZoom = useAppZoom();
  const dispatch = useDispatch();
  const sequenceCanvasCommand = useSelector(selectSequenceCanvasCommand);
  const localizedEntries = useSelector(
    (state: RootState) => state.trainingLocalization?.localizedEntries ?? {},
  );
  const currentLanguageShortcode = useSelector(
    (state: RootState) =>
      state.trainingLocalization?.currentLanguage?.shortcode ?? "nl",
  );
  const actors = useSelector(selectActors);
  const propPresets = useSelector(selectAllPropPresets);
  const selectedElementType = useSelector(selectSelectedElementType);

  const {
    sequenceId,
    nodes,
    edges,
    measuredNodeSizesRef,
    commentZones,
    textboxes,
    menu,
    pendingConnection,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onPaneContextMenu,
    onCloseMenu,
    onAddNode,
    onDeleteEdges,
    onCreateCommentZone,
    onDeleteCommentZones,
    onUpdateCommentZone,
    onCreateTextbox,
    onDeleteTextboxes,
    onDeleteSelection,
    onSetSelectionColor,
    onUpdateTextbox,
    setLocalNodes,
    setLocalCommentZones,
    setLocalTextboxes,
    persistCanvasState,
    onConnectStart,
    onConnectEnd,
    onEdgeClick,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onEdgePathGeometryChange,
    onEdgePathGeometryRemove,
  } = useSequenceCanvas();
  const savedSequenceCanvasViewport = useSelector((state: RootState) =>
    selectSequenceCanvasViewport(state, sequenceId),
  );
  const { applyLayout } = useSequenceLayout();

  const {
    selection,
    clearSelection,
    replaceSelection,
    addToSelection,
    toggleSelection,
    setSelection,
    setNodeSelection,
    selectedNodes,
  } = useSequenceCanvasSelection({
    sequenceId,
    nodes,
    commentZones,
    textboxes,
  });
  const [selectedEdges, setSelectedEdges] = useState<Edge[]>([]);
  const selectedCommentZoneIds = selection.commentZoneIds;
  const selectedTextboxIds = selection.textboxIds;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);
  const [searchFocusRequestId, setSearchFocusRequestId] = useState(0);
  const {
    handleCopy,
    handleCut,
    handlePaste,
    handleDuplicate,
    setSequenceCanvasRef,
    onPaneMouseMove,
  } = useSequenceCopyPaste(
    selectedNodes,
    selectedCommentZoneIds,
    selectedTextboxIds,
    setSelection,
  );

  const isItemSelected = useCallback(
    (item: SequenceCanvasItem) => {
      const ids =
        item.kind === "node"
          ? selection.nodeIds
          : item.kind === "commentZone"
            ? selection.commentZoneIds
            : selection.textboxIds;
      return ids.includes(item.id);
    },
    [selection],
  );

  const handleSelectItem = useCallback(
    (
      item: SequenceCanvasItem,
      mode: SelectionInputMode,
      options?: { preserveIfSelected?: boolean },
    ) => {
      if (mode === "replace") {
        setSelectedEdges([]);
        if (options?.preserveIfSelected && isItemSelected(item)) return;
        replaceSelection(item);
        return;
      }

      if (mode === "add") {
        addToSelection(item);
        return;
      }

      toggleSelection(item);
    },
    [addToSelection, isItemSelected, replaceSelection, toggleSelection],
  );

  const cuttableNodeCount = useMemo(
    () =>
      selectedNodes.filter(
        (node) =>
          node.id !== TEMP_TARGET_NODE_ID &&
          !isSequenceRootNode(node) &&
          !isProtectedTakeSequenceTrigger(node, selectedElementType),
      ).length,
    [selectedElementType, selectedNodes],
  );

  const getLocalizedValue = useCallback(
    (key: string) =>
      localizedEntries[key]?.[currentLanguageShortcode]?.value ?? "",
    [currentLanguageShortcode, localizedEntries],
  );

  const searchableNodes = useMemo(
    () =>
      nodes.filter(
        (node): node is SequenceNodeType => node.id !== TEMP_TARGET_NODE_ID,
      ),
    [nodes],
  );
  const searchLookupContext = useMemo(
    () => createSearchLookupContext(actors, propPresets),
    [actors, propPresets],
  );

  const searchMatches = useMemo(
    () =>
      findSequenceCanvasMatches({
        nodes: searchableNodes,
        commentZones,
        textboxes,
        query: searchQuery,
        getValue: getLocalizedValue,
        searchLookupContext,
      }),
    [
      commentZones,
      getLocalizedValue,
      searchLookupContext,
      searchQuery,
      searchableNodes,
      textboxes,
    ],
  );
  const activeSearchMatch =
    searchMatches.length > 0
      ? searchMatches[activeSearchIndex % searchMatches.length]
      : null;

  const singleSelectedNodeId =
    selectedNodes.length === 1 ? selectedNodes[0].id : undefined;
  useEffect(() => {
    dispatch(
      setSequenceCanvasSelection({
        nodeCount: selectedNodes.length,
        ...(singleSelectedNodeId ? { singleNodeId: singleSelectedNodeId } : {}),
        commentZoneCount: selectedCommentZoneIds.length,
        textboxCount: selectedTextboxIds.length,
        cuttableCount:
          cuttableNodeCount +
          selectedCommentZoneIds.length +
          selectedTextboxIds.length,
      }),
    );

    return () => {
      dispatch(
        setSequenceCanvasSelection({
          nodeCount: 0,
          commentZoneCount: 0,
          textboxCount: 0,
          cuttableCount: 0,
        }),
      );
    };
  }, [
    cuttableNodeCount,
    dispatch,
    selectedCommentZoneIds.length,
    selectedNodes.length,
    selectedTextboxIds.length,
    singleSelectedNodeId,
  ]);

  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isSearchOpen]);

  useEffect(() => {
    if (searchMatches.length === 0) {
      setActiveSearchIndex(0);
      return;
    }

    setActiveSearchIndex((currentIndex) => currentIndex % searchMatches.length);
  }, [searchMatches.length]);

  useEffect(() => {
    if (!isSearchOpen || !activeSearchMatch || searchFocusRequestId === 0) {
      return;
    }

    if (activeSearchMatch.kind !== "node") {
      return;
    }

    dispatch(setFocusedSequenceNodeId(activeSearchMatch.id));
    replaceSelection({ kind: "node", id: activeSearchMatch.id });
    setSelectedEdges([]);
  }, [
    activeSearchMatch,
    dispatch,
    isSearchOpen,
    searchFocusRequestId,
    replaceSelection,
  ]);

  const handleSearchMatchFocused = useCallback(() => {
    if (!activeSearchMatch) {
      return;
    }

    if (activeSearchMatch.kind === "commentZone") {
      setSelectedEdges([]);
      replaceSelection({
        kind: "commentZone",
        id: activeSearchMatch.commentZone.id,
      });
    } else if (activeSearchMatch.kind === "textbox") {
      setSelectedEdges([]);
      replaceSelection({ kind: "textbox", id: activeSearchMatch.textbox.id });
    }

    setSearchFocusRequestId(0);
  }, [activeSearchMatch, replaceSelection]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.key !== "Delete" && event.key !== "Backspace") ||
        isEditableElement(event.target)
      ) {
        return;
      }

      const result = onDeleteSelection(
        selection,
        selectedEdges.map((edge) => edge.id),
      );
      if (!result.didPersist) return;

      event.preventDefault();
      event.stopPropagation();
      const removedNodeIds = new Set(result.removedSelection.nodeIds);
      const removedCommentZoneIds = new Set(
        result.removedSelection.commentZoneIds,
      );
      const removedTextboxIds = new Set(result.removedSelection.textboxIds);
      setSelection({
        nodeIds: selection.nodeIds.filter((id) => !removedNodeIds.has(id)),
        commentZoneIds: selection.commentZoneIds.filter(
          (id) => !removedCommentZoneIds.has(id),
        ),
        textboxIds: selection.textboxIds.filter(
          (id) => !removedTextboxIds.has(id),
        ),
      });
      setSelectedEdges([]);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDeleteSelection, selection, selectedEdges, setSelection]);

  useShortcut(ShortCuts.Copy, handleCopy);
  useShortcut(ShortCuts.Cut, handleCut);
  useShortcut(ShortCuts.Paste, handlePaste);
  useShortcut(ShortCuts.Duplicate, handleDuplicate);

  useEffect(() => {
    const handleFormatShortcut = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() !== "f" ||
        !event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isEditableElement(event.target)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      applyLayout();
    };

    window.addEventListener("keydown", handleFormatShortcut);
    return () => window.removeEventListener("keydown", handleFormatShortcut);
  }, [applyLayout]);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setActiveSearchIndex(0);
    setSearchFocusRequestId(0);
  }, []);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const moveToSearchMatch = useCallback(
    (direction: 1 | -1) => {
      if (searchMatches.length === 0) {
        return;
      }

      setActiveSearchIndex((currentIndex) => {
        const nextIndex =
          (currentIndex + direction + searchMatches.length) %
          searchMatches.length;
        return nextIndex;
      });
      setSearchFocusRequestId((current) => current + 1);
    },
    [searchMatches.length],
  );

  const selectedNodeIds = useMemo(
    () => new Set(selectedNodes.map((node) => node.id)),
    [selectedNodes],
  );

  const selectionGestureRef = useRef<{
    startScreen: { x: number; y: number };
    mode: SelectionInputMode;
    initialSelection: SequenceCanvasSelection;
    initialEdges: Edge[];
    latestNodeIds: string[];
    latestEdges: Edge[];
    ended: boolean;
  } | null>(null);
  const selectionGestureFinalizeTimerRef = useRef<number | null>(null);
  const selectionCallbackOverrideRef = useRef<{
    selection: SequenceCanvasSelection;
    edges: Edge[];
  } | null>(null);
  const selectionCallbackOverrideTimerRef = useRef<number | null>(null);

  const holdSelectionCallbackOverride = useCallback(
    (nextSelection: SequenceCanvasSelection, nextEdges: Edge[]) => {
      selectionCallbackOverrideRef.current = {
        selection: nextSelection,
        edges: nextEdges,
      };
      if (selectionCallbackOverrideTimerRef.current !== null) {
        window.clearTimeout(selectionCallbackOverrideTimerRef.current);
      }
      selectionCallbackOverrideTimerRef.current = window.setTimeout(() => {
        selectionCallbackOverrideRef.current = null;
        selectionCallbackOverrideTimerRef.current = null;
      }, 0);
    },
    [],
  );

  useEffect(
    () => () => {
      if (selectionGestureFinalizeTimerRef.current !== null) {
        window.clearTimeout(selectionGestureFinalizeTimerRef.current);
      }
      if (selectionCallbackOverrideTimerRef.current !== null) {
        window.clearTimeout(selectionCallbackOverrideTimerRef.current);
      }
    },
    [],
  );

  const handleSelectionStart = useCallback(
    (event: ReactMouseEvent) => {
      selectionGestureRef.current = {
        startScreen: { x: event.clientX, y: event.clientY },
        mode: getSelectionInputMode(event),
        initialSelection: {
          nodeIds: [...selection.nodeIds],
          commentZoneIds: [...selection.commentZoneIds],
          textboxIds: [...selection.textboxIds],
        },
        initialEdges: [...selectedEdges],
        latestNodeIds: [],
        latestEdges: [],
        ended: false,
      };
      // React Flow resets only the selection it currently sees. Local node
      // flags can still contain older selections hidden by nodesWithSelection.
      // Clear those before applying this marquee's incremental hit changes.
      onNodesChange(
        nodes
          .filter((node) => node.selected)
          .map((node) => ({
            id: node.id,
            type: "select" as const,
            selected: false,
          })),
      );
    },
    [nodes, onNodesChange, selectedEdges, selection],
  );

  const handleSelectionChange = useCallback(
    (nextSelection: { nodes: Node[]; edges: Edge[] }) => {
      const gesture = selectionGestureRef.current;
      if (gesture) {
        const nextNodeIds = nextSelection.nodes.map((node) => node.id);
        const nextEdges = nextSelection.edges ?? [];

        // React Flow can publish the real marquee result just after
        // onSelectionEnd. Keep accepting non-empty late results, but do not
        // let a trailing empty callback erase a result already observed.
        if (
          !gesture.ended ||
          nextNodeIds.length > 0 ||
          gesture.latestNodeIds.length === 0
        ) {
          gesture.latestNodeIds = nextNodeIds;
        }
        if (
          !gesture.ended ||
          nextEdges.length > 0 ||
          gesture.latestEdges.length === 0
        ) {
          gesture.latestEdges = nextEdges;
        }
        return;
      }

      const callbackOverride = selectionCallbackOverrideRef.current;
      if (callbackOverride) {
        setSelection(callbackOverride.selection);
        setSelectedEdges(callbackOverride.edges);
        return;
      }

      setNodeSelection(nextSelection.nodes.map((node) => node.id));
      setSelectedEdges(nextSelection.edges ?? []);
    },
    [setNodeSelection, setSelection],
  );

  const handleSelectionEnd = useCallback(
    (event: ReactMouseEvent) => {
      const gesture = selectionGestureRef.current;
      if (!gesture) return;
      gesture.ended = true;
      const endScreen = { x: event.clientX, y: event.clientY };

      if (selectionGestureFinalizeTimerRef.current !== null) {
        window.clearTimeout(selectionGestureFinalizeTimerRef.current);
      }
      selectionGestureFinalizeTimerRef.current = window.setTimeout(() => {
        selectionGestureFinalizeTimerRef.current = null;
        if (selectionGestureRef.current !== gesture) return;
        selectionGestureRef.current = null;

        const flowRect = normalizeFlowRect(
          screenToFlowPosition(gesture.startScreen),
          screenToFlowPosition(endScreen),
        );
        const annotationHits = getAnnotationIdsInMarquee(
          flowRect,
          commentZones,
          textboxes,
        );
        const additive = gesture.mode !== "replace";
        const nextSelection: SequenceCanvasSelection = {
          nodeIds: additive
            ? [...gesture.initialSelection.nodeIds, ...gesture.latestNodeIds]
            : gesture.latestNodeIds,
          commentZoneIds: additive
            ? [
                ...gesture.initialSelection.commentZoneIds,
                ...annotationHits.commentZoneIds,
              ]
            : annotationHits.commentZoneIds,
          textboxIds: additive
            ? [
                ...gesture.initialSelection.textboxIds,
                ...annotationHits.textboxIds,
              ]
            : annotationHits.textboxIds,
        };
        const nextEdges = additive
          ? [
              ...gesture.initialEdges,
              ...gesture.latestEdges.filter(
                (edge) =>
                  !gesture.initialEdges.some(
                    (initialEdge) => initialEdge.id === edge.id,
                  ),
              ),
            ]
          : gesture.latestEdges;
        setSelection(nextSelection);
        setSelectedEdges(nextEdges);
        holdSelectionCallbackOverride(nextSelection, nextEdges);
      }, 0);
    },
    [
      commentZones,
      holdSelectionCallbackOverride,
      screenToFlowPosition,
      setSelection,
      textboxes,
    ],
  );

  const handleNodeClick = useCallback(
    (event: ReactMouseEvent, node: Node) => {
      const mode = getSelectionInputMode(event);
      // Shift+click: this node and everything after it. Ctrl/Cmd+click still adds or removes one node.
      if (mode === "add") {
        const nextSelection: SequenceCanvasSelection = {
          nodeIds: getNodeAndAfter(node.id, edges),
          commentZoneIds: [],
          textboxIds: [],
        };
        setSelection(nextSelection);
        setSelectedEdges([]);
        holdSelectionCallbackOverride(nextSelection, []);
        return;
      }
      const nextNodeIds =
        mode === "replace"
          ? [node.id]
          : selection.nodeIds.includes(node.id)
            ? selection.nodeIds.filter((id) => id !== node.id)
            : [...selection.nodeIds, node.id];
      const nextSelection: SequenceCanvasSelection =
        mode === "replace"
          ? { nodeIds: nextNodeIds, commentZoneIds: [], textboxIds: [] }
          : { ...selection, nodeIds: nextNodeIds };
      const nextEdges = mode === "replace" ? [] : selectedEdges;
      setSelection(nextSelection);
      setSelectedEdges(nextEdges);
      holdSelectionCallbackOverride(nextSelection, nextEdges);
    },
    [edges, holdSelectionCallbackOverride, selectedEdges, selection, setSelection],
  );

  const handleEdgeSelectionClick = useCallback(
    (event: ReactMouseEvent, edge: Edge) => {
      if (getSelectionInputMode(event) !== "replace") return;

      const nextSelection: SequenceCanvasSelection = {
        nodeIds: [],
        commentZoneIds: [],
        textboxIds: [],
      };
      const nextEdges = [edge];
      setSelection(nextSelection);
      setSelectedEdges(nextEdges);
      holdSelectionCallbackOverride(nextSelection, nextEdges);
    },
    [holdSelectionCallbackOverride, setSelection],
  );

  const handleNodeDragStart = useCallback(
    (event: ReactMouseEvent, node: Node<SequenceNodeData>) => {
      handleSelectItem(
        { kind: "node", id: node.id },
        getSelectionInputMode(event),
        { preserveIfSelected: true },
      );
      onNodeDragStart(event, node);
    },
    [handleSelectItem, onNodeDragStart],
  );

  const isTakeSequence = isTakeSequenceEditor(selectedElementType);
  const nodesWithSelection = useMemo(
    () => {
      const withSelection = nodes.map((node) => {
        const isMarqueeSelected =
          selectionGestureRef.current !== null && Boolean(node.selected);
        const isSearchSelected =
          Boolean(isSearchOpen && activeSearchMatch?.kind === "node") &&
          activeSearchMatch?.id === node.id &&
          selectedCommentZoneIds.length === 0 &&
          selectedTextboxIds.length === 0 &&
          selectedEdges.length === 0;

        return {
          ...node,
          // A size React Flow reported but this render hasn't applied yet; see the hook.
          measured: measuredNodeSizesRef.current.get(node.id) ?? node.measured,
          selected:
            isMarqueeSelected ||
            (selectionGestureRef.current === null &&
              (selectedNodeIds.has(node.id) || isSearchSelected)),
        };
      });
      return isTakeSequence
        ? placeDefaultTakeEnd(withSelection, (node) => node.measured?.height)
        : withSelection;
    },
    [
      activeSearchMatch,
      isSearchOpen,
      isTakeSequence,
      measuredNodeSizesRef,
      nodes,
      selectedCommentZoneIds.length,
      selectedEdges.length,
      selectedNodeIds,
      selectedTextboxIds.length,
    ],
  );

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      sequence: SequenceNode,
    }),
    [],
  );

  const edgeTypes = useMemo(
    () => ({
      custom: SequenceCustomEdge,
    }),
    [],
  );

  // Function to compute edge colors: only direct onTrue/onFalse edges get green/red
  const computeEdgeColors = useMemo(() => {
    const edgeColorMap = new Map<string, "green" | "red">();
    edges.forEach((edge) => {
      // if (edge.sourceHandle === "onTrue") edgeColorMap.set(edge.id, "green");
      // else if (edge.sourceHandle === "onFalse")
      //   edgeColorMap.set(edge.id, "red");
    });
    return edgeColorMap;
  }, [edges]);

  // Map node id -> custom color for edge coloring from source node
  const nodeIdToColor = useMemo(() => {
    const map = new Map<string, string>();
    nodes.forEach((node) => {
      if (node.data?.color) {
        map.set(node.id, node.data.color);
      }
    });
    return map;
  }, [nodes]);

  // Enhance edges with onEdgeClick handler and selection info
  const edgesWithHandlers = useMemo(() => {
    // Check if exactly one edge is selected and no nodes are selected
    const exactlyOneEdgeSelected =
      selectedEdges.length === 1 && selectedNodes.length === 0;
    // Connectors are muted unless they belong to what you are working on.
    const selectedNodeIds = new Set(selectedNodes.map((node) => node.id));

    return edges.map((edge) => {
      // Prefer source/target node custom colors for gradient; fallback to path or default
      const sourceNodeColor = nodeIdToColor.get(edge.source);
      const targetNodeColor = nodeIdToColor.get(edge.target);
      const pathColor = computeEdgeColors.get(edge.id);
      const pathColorHex =
        pathColor === "green"
          ? "#8FBC8F"
          : pathColor === "red"
            ? "#D35F5F"
            : undefined;
      const edgeColor = sourceNodeColor ?? pathColorHex;

      return {
        ...edge,
        data: {
          ...edge.data,
          sourceHandle: edge.sourceHandle,
          color: edgeColor,
          sourceColor: sourceNodeColor ?? pathColorHex,
          targetColor: targetNodeColor ?? pathColorHex,
          highlighted:
            !!edge.selected ||
            selectedNodeIds.has(edge.source) ||
            selectedNodeIds.has(edge.target),
          onEdgeClick: (edgeId: string, clientX: number, clientY: number) => {
            // Get bounds for menu positioning
            const bounds = reactFlowWrapper.current?.getBoundingClientRect();
            if (!bounds) return;

            // Convert client coordinates to relative coordinates within the canvas
            const relativeX = clientX - bounds.left;
            const relativeY = clientY - bounds.top;

            // Call the handler with both relative and screen coordinates
            // The hook needs screen coordinates for screenToFlowPosition conversion
            onEdgeClick(edgeId, relativeX, relativeY, clientX, clientY);
          },
          onEdgePathGeometryChange,
          onEdgePathGeometryRemove,
          // Pass whether exactly one edge is selected (and this is it)
          showInsertButton:
            exactlyOneEdgeSelected && selectedEdges[0]?.id === edge.id,
        },
      };
    });
  }, [
    edges,
    onEdgeClick,
    selectedEdges,
    selectedNodes,
    computeEdgeColors,
    nodeIdToColor,
    onEdgePathGeometryChange,
    onEdgePathGeometryRemove,
  ]);

  const handlePaneContextMenu = (event: React.MouseEvent) => {
    const bounds = reactFlowWrapper.current?.getBoundingClientRect();
    if (!bounds) return;
    onPaneContextMenu(event, bounds);
  };

  const onEdgesDelete = useCallback(
    (edgesToDelete: Edge[]) => {
      onDeleteEdges(edgesToDelete);
    },
    [onDeleteEdges],
  );

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      (
        reactFlowWrapper as React.MutableRefObject<HTMLDivElement | null>
      ).current = el;
      setSequenceCanvasRef(el);
    },
    [setSequenceCanvasRef],
  );

  const handleCanvasKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (
        event.key.toLowerCase() === "f" &&
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        !event.shiftKey &&
        !isEditableElement(event.target)
      ) {
        event.preventDefault();
        event.stopPropagation();
        openSearch();
        return;
      }

      if (event.key === "Escape" && isSearchOpen) {
        event.preventDefault();
        event.stopPropagation();
        closeSearch();
      }
    },
    [closeSearch, isSearchOpen, openSearch],
  );

  const handleSearchInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeSearch();
        reactFlowWrapper.current?.focus();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        moveToSearchMatch(event.shiftKey ? -1 : 1);
      }
    },
    [closeSearch, moveToSearchMatch],
  );

  return (
    <SequenceCanvasLodProvider>
      <div
        className="flex relative flex-col w-full h-full bg-creator-primary-background focus:outline-none"
        ref={setRef}
        tabIndex={0}
        role="application"
        aria-label="Sequence canvas"
        onMouseMove={onPaneMouseMove}
        onKeyDown={handleCanvasKeyDown}
      >
        <span className="sr-only" aria-live="polite">
          {`${selection.nodeIds.length} nodes, ${selection.commentZoneIds.length} comment boxes, ${selection.textboxIds.length} text notes selected`}
        </span>
        {isSearchOpen && (
          <div
            className="absolute left-1/2 top-4 z-20 flex min-w-[320px] -translate-x-1/2 items-center gap-2 rounded-xl border-2 border-creator-primary bg-creator-surface px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.2)]"
            data-testid="sequence-canvas-search"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setActiveSearchIndex(0);
                setSearchFocusRequestId((current) => current + 1);
              }}
              onKeyDown={handleSearchInputKeyDown}
              className="min-w-0 flex-1 bg-transparent text-sm text-creator-text-main outline-none"
              placeholder="Search canvas"
              aria-label="Search canvas"
            />
            <span
              className="min-w-[64px] text-right text-xs text-creator-text-sub"
              aria-live="polite"
            >
              {searchMatches.length > 0
                ? `${activeSearchIndex + 1} / ${searchMatches.length}`
                : "0 / 0"}
            </span>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-xs text-creator-text-main hover:bg-creator-primary-washed disabled:opacity-40"
              aria-label="Previous search result"
              onClick={() => moveToSearchMatch(-1)}
              disabled={searchMatches.length === 0}
            >
              Prev
            </button>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-xs text-creator-text-main hover:bg-creator-primary-washed disabled:opacity-40"
              aria-label="Next search result"
              onClick={() => moveToSearchMatch(1)}
              disabled={searchMatches.length === 0}
            >
              Next
            </button>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md text-creator-text-main hover:bg-creator-primary-washed"
              aria-label="Close search"
              onClick={closeSearch}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        )}
        {(selectedNodes.length > 0 ||
          selectedCommentZoneIds.length > 0 ||
          selectedTextboxIds.length > 0) && (
          <SequenceSelectionColorToolbar
            selectedNodes={selectedNodes}
            selectedCommentZoneIds={selectedCommentZoneIds}
            selectedTextboxIds={selectedTextboxIds}
            onSetSelectionColor={(
              nodeIds,
              commentZoneIds,
              textboxIds,
              color,
            ) => {
              onSetSelectionColor(
                { nodeIds, commentZoneIds, textboxIds },
                color,
              );
              clearSelection();
            }}
          />
        )}
        {/* React Flow's pointer math doesn't survive a zoomed ancestor, so undo the
            automatic screen scale here; the canvas has its own zoom anyway. */}
        <div
          className="flex-1 min-h-0"
          style={appZoom === 1 ? undefined : { zoom: 1 / appZoom }}
        >
          <ReactFlow
            nodes={nodesWithSelection}
            edges={edgesWithHandlers}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={(event, connectionState) =>
              onConnectEnd(event, connectionState, reactFlowWrapper.current)
            }
            onNodeDragStart={handleNodeDragStart}
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            fitView={!savedSequenceCanvasViewport}
            fitViewOptions={fitViewOptions}
            defaultViewport={savedSequenceCanvasViewport ?? undefined}
            minZoom={MIN_SEQUENCE_CANVAS_ZOOM}
            proOptions={{ hideAttribution: true }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onPaneContextMenu={(event) =>
              handlePaneContextMenu(
                event as React.MouseEvent<Element, MouseEvent>,
              )
            }
            onEdgesDelete={onEdgesDelete}
            connectionLineStyle={{ stroke: "#3AC4FF", strokeWidth: 2 }}
            connectionRadius={64}
            selectionOnDrag={true}
            panOnDrag={[1]}
            selectionMode={SelectionMode.Partial}
            onSelectionStart={handleSelectionStart}
            onSelectionChange={handleSelectionChange}
            onSelectionEnd={handleSelectionEnd}
            onNodeClick={handleNodeClick}
            onEdgeClick={handleEdgeSelectionClick}
            onPaneClick={() => {
              clearSelection();
              setSelectedEdges([]);
            }}
            onMoveEnd={(_, viewport) => {
              dispatch(
                setSequenceCanvasViewport({
                  sequenceId,
                  viewport: {
                    x: viewport.x,
                    y: viewport.y,
                    zoom: viewport.zoom,
                  },
                }),
              );
            }}
            deleteKeyCode={null}
            disableKeyboardA11y={true}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={24}
              size={3}
              color="var(--pi-canvas-dots)"
            />
            {SHOW_SEQUENCE_MINIMAP && <MiniMap nodeStrokeWidth={3} pannable zoomable />}
            <SequenceCommentZoneLayer
              sequenceId={sequenceId}
              nodes={nodes}
              commentZones={commentZones}
              textboxes={textboxes}
              selectedNodes={selectedNodes}
              selectedCommentZoneIds={selectedCommentZoneIds}
              onSelectItem={handleSelectItem}
              onCreateCommentZone={onCreateCommentZone}
              onUpdateCommentZone={onUpdateCommentZone}
              onDeleteCommentZones={(zoneIds) => {
                onDeleteCommentZones(zoneIds);
              }}
              setLocalNodes={setLocalNodes}
              setLocalCommentZones={setLocalCommentZones}
              setLocalTextboxes={setLocalTextboxes}
              persistCanvasState={persistCanvasState}
            />
            <SequenceTextboxLayer
              sequenceId={sequenceId}
              textboxes={textboxes}
              selectedTextboxIds={selectedTextboxIds}
              onSelectItem={handleSelectItem}
              onCreateTextbox={onCreateTextbox}
              onDeleteTextboxes={(textboxIds) => {
                onDeleteTextboxes(textboxIds);
              }}
              onUpdateTextbox={onUpdateTextbox}
              setLocalTextboxes={setLocalTextboxes}
              persistCanvasState={persistCanvasState}
            />
            <SequenceContextMenu
              position={menu}
              onAddNode={onAddNode}
              onClose={onCloseMenu}
              pendingConnection={pendingConnection}
            />
            <SequenceCanvasCommandBridge
              commandType={sequenceCanvasCommand.type}
              commandRequestId={sequenceCanvasCommand.requestId}
              wrapperRef={reactFlowWrapper}
              selectedNodes={selectedNodes}
              handleCopy={handleCopy}
              handleCut={handleCut}
              handlePaste={handlePaste}
              handleDuplicate={handleDuplicate}
              onFormatLayout={applyLayout}
              onCreateCommentZone={onCreateCommentZone}
              onCreateTextbox={onCreateTextbox}
              setSelection={setSelection}
            />
            <FocusNodeHandler />
            <RemeasureNodesWithoutHandles />
            <FocusCanvasSearchMatchHandler
              activeSearchMatch={activeSearchMatch}
              searchFocusRequestId={searchFocusRequestId}
              onFocused={handleSearchMatchFocused}
            />
            <SequenceCanvasViewportPersistence
              sequenceId={sequenceId}
              savedViewport={savedSequenceCanvasViewport}
            />
          </ReactFlow>
        </div>
      </div>
    </SequenceCanvasLodProvider>
  );
}
